/**
 * NetcodeAllocPanel — live GRAPHS for the netcode + alloc view of the
 * DiagnosticHub (DJ ask, 2026-06-27: "make sure we have a graph and alloc in
 * our netcode + debug hub" — the hub was all text/lists before).
 *
 * Samples client-side every 500ms into rolling ~30s buffers and draws sparklines:
 *   - Bandwidth OUT / IN (B/s) + packet rate (pkts/s) from window.nm's tracer
 *     (getRecentTraffic) — the "are packets getting tangled?" view.
 *   - Heap MB over time (performance.memory, Chromium) — the alloc/leak view
 *     (a climbing line = a leak; sawtooth = healthy GC).
 *   - Top alloc-counter movers (window.__ALLOC_COUNTERS__, always-on via the
 *     FreezeAttributor) — which hot path is churning, ranked per-second.
 *
 * All sources are guarded — no game session / non-Chromium / counters-off each
 * degrade to "n/a" rather than throwing.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Sparkline } from '../../components/Sparkline';
import { computeTopMovers } from '../../../diagnostics/freezeAttribution';

const SAMPLE_MS = 500;
const WINDOW = 60; // 60 samples × 500ms = 30s of history

function fmtBps(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M/s`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K/s`;
  return `${Math.round(v)}B/s`;
}

interface Mover { key: string; perSec: number }

export const NetcodeAllocPanel: React.FC = () => {
  const [, force] = useState(0);
  const out = useRef<number[]>([]);
  const inc = useRef<number[]>([]);
  const pkts = useRef<number[]>([]);
  const heap = useRef<number[]>([]);
  const moverSeries = useRef<number[]>([]);
  const movers = useRef<Mover[]>([]);
  const prevCounters = useRef<Record<string, number> | null>(null);
  const supported = useRef({ bw: false, heap: false, alloc: false });

  useEffect(() => {
    const push = (buf: React.MutableRefObject<number[]>, v: number) => {
      buf.current.push(v);
      if (buf.current.length > WINDOW) buf.current.shift();
    };
    const id = setInterval(() => {
      // — Bandwidth + packet rate (last 1s of the client tracer) —
      try {
        const nm = (window as any).nm;
        const evts: any[] = nm?.getRecentTraffic?.(300) ?? [];
        if (evts.length) {
          supported.current.bw = true;
          const cutoff = Date.now() - 1000;
          let o = 0, i = 0, p = 0;
          for (const e of evts) {
            const t = Date.parse(e.timestamp);
            if (Number.isFinite(t) && t < cutoff) continue;
            p++;
            const d = String(e.direction).toLowerCase();
            if (d.includes('out') || d.includes('send') || d.includes('sent')) o += e.bytes || 0;
            else i += e.bytes || 0;
          }
          push(out, o); push(inc, i); push(pkts, p);
        }
      } catch { /* no session */ }

      // — Heap (Chromium) —
      try {
        const mem = (performance as any).memory;
        if (mem?.usedJSHeapSize) {
          supported.current.heap = true;
          push(heap, Math.round(mem.usedJSHeapSize / 1048576));
        }
      } catch { /* unsupported */ }

      // — Alloc top movers (always-on AllocCounters) —
      try {
        const ac = (window as any).__ALLOC_COUNTERS__;
        const counters: Record<string, number> | undefined = ac?.getStats?.().counters;
        if (counters) {
          supported.current.alloc = true;
          const prev = prevCounters.current;
          if (prev) {
            const top = computeTopMovers(prev, counters, SAMPLE_MS, 6);
            movers.current = top;
            push(moverSeries, top[0]?.perSec ?? 0);
          }
          prevCounters.current = counters;
        }
      } catch { /* counters off */ }

      force((x) => x + 1);
    }, SAMPLE_MS);
    return () => clearInterval(id);
  }, []);

  const box: React.CSSProperties = { marginBottom: 14 };
  const head: React.CSSProperties = { fontSize: 11, fontWeight: 700, opacity: 0.8, margin: '6px 0 4px', letterSpacing: 0.4 };

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={box}>
        <div style={head}>📈 BANDWIDTH (last 30s, B/s)</div>
        {supported.current.bw ? (
          <>
            <Sparkline data={out.current} color="#ffb454" label="out" format={fmtBps} />
            <Sparkline data={inc.current} color="#4fc3f7" label="in" format={fmtBps} />
            <Sparkline data={pkts.current} color="#a5d6a7" label="pkts/s" format={(v) => `${Math.round(v)}/s`} />
          </>
        ) : (
          <div style={{ fontSize: 11, opacity: 0.6 }}>no game session in this tab</div>
        )}
      </div>

      <div style={box}>
        <div style={head}>🧠 HEAP (last 30s, MB — climbing = leak, sawtooth = healthy GC)</div>
        {supported.current.heap ? (
          <Sparkline data={heap.current} color="#ce93d8" label="heap" format={(v) => `${Math.round(v)} MB`} />
        ) : (
          <div style={{ fontSize: 11, opacity: 0.6 }}>performance.memory unavailable (non-Chromium)</div>
        )}
      </div>

      <div style={box}>
        <div style={head}>⚙️ ALLOC HOT-PATH MOVERS (per second)</div>
        {supported.current.alloc ? (
          <>
            <Sparkline data={moverSeries.current} color="#ef9a9a" label="top mover" format={(v) => `${Math.round(v)}/s`} />
            <div style={{ fontSize: 11, fontFamily: 'monospace', marginTop: 4 }}>
              {movers.current.length === 0 && <div style={{ opacity: 0.6 }}>collecting…</div>}
              {movers.current.map((m) => (
                <div key={m.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ opacity: 0.85 }}>{m.key}</span>
                  <span style={{ color: '#ef9a9a' }}>{m.perSec}/s</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 11, opacity: 0.6 }}>AllocCounters not reporting</div>
        )}
      </div>
    </div>
  );
};
